import type { PostMediaItem } from '@ps/api/types';
import { ModerationStatusEnumItem } from '@ps/api/types';

import { PostMedia } from './media/PostMedia';

interface Props {
  media: PostMediaItem;
  /** Whether this is the visible slide — drives `PostMedia` video auto-pause. */
  active: boolean;
  focused: boolean;
  /** Shared carousel aspect ratio (`W / 1`) that drives the slide height. */
  aspectRatio: number;
  /** Slides past the free-preview window are blanked — hide from the a11y tree. */
  locked: boolean;
  /** Owner surface — show the "Rejected" overlay when this media was turned down. */
  editable?: boolean;
  /** Registers the slide element for the carousel's IntersectionObserver + scroll. */
  slideRef: (el: HTMLDivElement | null) => void;
}

/**
 * One carousel cell — a fixed full-width snap slide sized by the shared aspect
 * ratio, wrapping the {@link PostMedia} leaf. Image fills the box (cover); video /
 * live-replay preserve their frame (contain). 1:1 with legacy `Slide` in
 * `flows/bio/wall/components/post/slide-view.tsx`.
 */
export function Slide({
  media,
  active,
  focused,
  aspectRatio,
  locked,
  editable,
  slideRef,
}: Props) {
  // Image: cover (fills the box, may crop). Video / live replay: contain
  // (preserves the full frame, may letterbox).
  const fitClass = media.type === 'IMAGE' ? 'object-cover' : 'object-contain';
  const rejected =
    editable === true &&
    media.moderation?.moderationStatus === ModerationStatusEnumItem.Rejected;
  return (
    <div
      ref={slideRef}
      // `basis-full shrink-0 grow-0` = `flex: 0 0 100%` — each slide is exactly
      // one scroller-viewport wide. `aspectRatio` drives the height from the width.
      className="bg-surface-1 relative shrink-0 grow-0 basis-full snap-start"
      style={{ aspectRatio }}
      aria-hidden={locked ? 'true' : undefined}
    >
      <PostMedia
        media={media}
        active={active}
        focused={focused}
        className={`h-full w-full ${fitClass}`}
      />
      {rejected ? (
        <div className="bg-danger text-content-on-danger pointer-events-none absolute right-0 top-0 flex max-w-[70%] items-center rounded-bl-[20px] py-[5px] pl-3 pr-2 text-[11px] font-medium">
          Rejected: does not follow content guidelines
        </div>
      ) : null}
    </div>
  );
}
